import { Badge } from '../../../../../../components/badge';
import { Button } from '../../../../../../components/buttons/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '../../../../../../components/dialog';
import { Tooltip } from '../../../../../../components/tooltip';
import {
  type Currency,
  SubscriptionPaymentCycle,
} from '../../../../../../entity/api/organization-billing';
import { tNamespace } from '../../../../../../services/translation';
import { formatCurrency } from '../../../../../../shared/currency';
import { formatDateToDdMmYyyy } from '../../../../../../shared/date';

const tBilling = tNamespace('app.billing.general');

export type ManagePlanModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  planName: string;
  billingCycle: SubscriptionPaymentCycle;
  renewsOnDate: number;
  monthlyPrice: number;
  yearlyPrice?: number;
  currency: Currency;
  discountPercent?: number;
  onEditPlan?: () => void;
  onCancelPlan?: () => void;
  onSkip?: () => void;
  userLanguage?: string;
  isEditPlanDisabled?: boolean;
};

export function ManagePlanModal(props: ManagePlanModalProps) {
  const {
    open,
    onOpenChange,
    planName,
    billingCycle,
    renewsOnDate,
    monthlyPrice,
    yearlyPrice,
    currency,
    discountPercent,
    onEditPlan,
    onCancelPlan,
    onSkip,
    userLanguage,
    isEditPlanDisabled = false,
  } = props;

  const isYearly = billingCycle === SubscriptionPaymentCycle.Year;
  const billingCycleText = tBilling(
    billingCycle === SubscriptionPaymentCycle.Year ? 'yearly' : 'monthly'
  );
  function handleEditPlan() {
    if (onEditPlan != null) {
      onEditPlan();
    }
  }

  function handleCancelPlan() {
    if (onCancelPlan != null) {
      onCancelPlan();
    }
  }

  function handleSkip() {
    if (onSkip != null) {
      onSkip();
    } else {
      onOpenChange(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent hasCloseButton className="w-[480px] max-w-full">
        <div className="flex flex-col gap-8">
          {/* Title */}
          <DialogTitle align="center">{tBilling('managePlan')}</DialogTitle>

          <div className="flex flex-col gap-4">
            {/* Divider */}
            <div className="h-px w-full bg-branding-gray-200" />

            {/* Plan Section */}
            <div className="flex flex-col gap-1">
              <p className="font-body-2-sb text-gray">{tBilling('plan')}</p>
              <p className="font-body-2-sb uppercase text-branding-accent-600">{planName}</p>
            </div>
            <div className="h-px w-full bg-branding-gray-200" />

            {/* Billing Cycle Section */}
            <div className="flex flex-col gap-1">
              <p className="font-body-2-sb text-gray">{tBilling('billingCycle')}</p>
              <p className="font-body-2-sb text-dark-gray">{billingCycleText}</p>
            </div>
            <div className="h-px w-full bg-branding-gray-200" />

            {/* Renews On Section */}
            <div className="flex flex-col gap-1">
              <p className="font-body-2-sb text-gray">{tBilling('renewsOn')}</p>
              <p className="font-body-2-sb text-dark-gray">
                {formatDateToDdMmYyyy(new Date(renewsOnDate))}
              </p>
            </div>

            {/* Divider */}
            <div className="h-px w-full bg-branding-gray-200" />

            {/* Total Section */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <div className="flex flex-col gap-1">
                  <p className="font-body-2-sb">{tBilling('total')}</p>
                  <div className="flex items-center gap-1">
                    {discountPercent != null && discountPercent > 0 ? (
                      <p className="font-display-4 text-gray line-through">
                        {formatCurrency(
                          monthlyPrice / (1 - discountPercent / 100),
                          currency,
                          userLanguage
                        )}
                      </p>
                    ) : null}
                    <p className="font-display-1 text-branding-accent-600">
                      {formatCurrency(monthlyPrice, currency, userLanguage)}
                    </p>
                    <p className="font-body-2-r text-branding-accent-600">
                      {tBilling('perMonthShort')}
                    </p>
                  </div>
                </div>
                {discountPercent != null && discountPercent > 0 ? (
                  <Badge size="sm" color="accent-contrast" className="uppercase">
                    {tBilling('percentOff', { percent: discountPercent })}
                  </Badge>
                ) : null}
              </div>
              {isYearly && yearlyPrice != null ? (
                <p className="font-body-3-r text-branding-gray-600">
                  {tBilling('billedAnnuallyAt', {
                    amount: formatCurrency(yearlyPrice, currency, userLanguage),
                  })}
                </p>
              ) : null}
            </div>
          </div>

          {/* Action Buttons */}
          <DialogFooter isColumn className="mt-2">
            <div className="flex w-full gap-3">
              {onEditPlan != null ? (
                isEditPlanDisabled ? (
                  <Tooltip text={tBilling('contactSupportToEditPlan')} className="flex-1">
                    <div className="flex-1">
                      <Button size="xl" variant="accent" className="w-full" disabled={true}>
                        {tBilling('editPlan')}
                      </Button>
                    </div>
                  </Tooltip>
                ) : (
                  <Button
                    size="xl"
                    variant="accent"
                    className="flex-1"
                    onClick={handleEditPlan}
                    disabled={isEditPlanDisabled}
                  >
                    {tBilling('editPlan')}
                  </Button>
                )
              ) : null}
              {onCancelPlan != null ? (
                <Button
                  size="xl"
                  variant="destructive"
                  className="flex-1"
                  onClick={handleCancelPlan}
                >
                  {tBilling('cancelPlan')}
                </Button>
              ) : null}
            </div>
            <Button size="xl" variant="ghost-gray" className="w-full" onClick={handleSkip}>
              {tBilling('skipForNow')}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
